import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";

const subjects = [
  "AI + Human Hybrid Development",
  "Web / Enterprise Software",
  "Cybersecurity Platform",
  "Brand & Digital Marketing",
  "Something else",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: subjects[0], message: "" });
  const [state, setState] = useState({ loading: false, ok: false, err: null });

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: false, err: null });
    try {
      await api.post("/contact", form);
      setState({ loading: false, ok: true, err: null });
      setForm({ name: "", email: "", phone: "", subject: subjects[0], message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail || "Something went wrong. Try again or email us directly.";
      setState({ loading: false, ok: false, err: typeof detail === "string" ? detail : "Validation failed." });
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HEADER */}
      <section className="relative border-b border-white/10 bg-[#050505] overflow-hidden">
        <div className="absolute inset-0 topo-grid opacity-30" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 py-24 md:py-28">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
            <div className="md:col-span-4">
              <p className="overline">Contact</p>
            </div>
            <div className="md:col-span-8">
              <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-black text-white tracking-[-0.03em] leading-[0.95]" data-testid="contact-title">
                Start the ascent.<br/>
                <span className="text-[#ff3333]">One message away.</span>
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-2xl leading-relaxed">
                Tell us what you're building. We reply within one business day with a scoped plan and a senior engineer on the line.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 md:py-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* DETAILS */}
          <div className="md:col-span-4 space-y-8">
            <div>
              <p className="overline mb-2">Direct channels</p>
              <p className="text-zinc-400 text-sm">No bots. Real people, senior engineers.</p>
            </div>

            <a href="mailto:reach@ridgecx.com" className="block border border-white/10 p-6 hover:border-[#ff3333] transition-colors group" data-testid="contact-email-card">
              <div className="flex items-center gap-3 text-[#ff3333]">
                <Mail size={20}/> <span className="overline text-[#ff3333]">Email</span>
              </div>
              <div className="mt-3 font-display text-xl font-bold text-white group-hover:text-[#ff3333] transition-colors">reach@ridgecx.com</div>
              <p className="mt-1 text-xs text-zinc-500 mono">Replies within 1 business day</p>
            </a>

            <a href="tel:+918960999515" className="block border border-white/10 p-6 hover:border-[#ff3333] transition-colors group" data-testid="contact-phone-card">
              <div className="flex items-center gap-3 text-[#ff3333]">
                <Phone size={20}/> <span className="overline text-[#ff3333]">Phone</span>
              </div>
              <div className="mt-3 font-display text-xl font-bold text-white group-hover:text-[#ff3333] transition-colors">+91-8960999515</div>
              <p className="mt-1 text-xs text-zinc-500 mono">Mon–Fri · 10:00–19:00 IST</p>
            </a>

            <div className="border border-white/10 p-6">
              <div className="flex items-center gap-3 text-[#ff3333]">
                <MapPin size={20}/> <span className="overline text-[#ff3333]">Corporate Office</span>
              </div>
              <div className="mt-3 font-display text-lg font-bold text-white leading-tight">
                Ridgeline Cyber Services LLP
              </div>
              <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
                8th Floor, Levana, Cyber Heights<br/>
                Unit No. 800 to 809, Vijaipur Colony<br/>
                Vibhuti Khand, Gomti Nagar<br/>
                Lucknow, Uttar Pradesh 226010
              </p>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Levana+Cyber+Heights+Vibhuti+Khand+Gomti+Nagar+Lucknow+226010"
                target="_blank"
                rel="noreferrer"
                className="mt-4 inline-flex items-center gap-1.5 text-xs mono text-[#ff3333] hover:text-white transition-colors"
                data-testid="contact-map-link"
              >
                OPEN IN MAPS →
              </a>
            </div>
          </div>

          {/* FORM */}
          <div className="md:col-span-8">
            <motion.form
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              onSubmit={submit}
              className="border border-white/10 bg-[#0a0a0a] p-6 md:p-10"
              data-testid="contact-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" required>
                  <input
                    required
                    value={form.name}
                    onChange={change("name")}
                    className="ridge-input"
                    placeholder="Your name"
                    data-testid="contact-input-name"
                  />
                </Field>
                <Field label="Email" required>
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={change("email")}
                    className="ridge-input"
                    placeholder="you@company.com"
                    data-testid="contact-input-email"
                  />
                </Field>
                <Field label="Phone">
                  <input
                    value={form.phone}
                    onChange={change("phone")}
                    className="ridge-input"
                    placeholder="+91 90000 00000"
                    data-testid="contact-input-phone"
                  />
                </Field>
                <Field label="Subject" required>
                  <select
                    required
                    value={form.subject}
                    onChange={change("subject")}
                    className="ridge-input"
                    data-testid="contact-select-subject"
                  >
                    {subjects.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>

              <div className="mt-6">
                <Field label="Message" required>
                  <textarea
                    required
                    rows={7}
                    value={form.message}
                    onChange={change("message")}
                    className="ridge-input resize-none"
                    placeholder="Tell us about your project, timeline, and what success looks like."
                    data-testid="contact-input-message"
                  />
                </Field>
              </div>

              <div className="mt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <p className="text-xs text-zinc-500 mono">WE RESPECT YOUR INBOX. NO MARKETING SPAM. EVER.</p>
                <button
                  type="submit"
                  disabled={state.loading}
                  className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="contact-submit-button"
                >
                  {state.loading ? "Sending…" : <>Send message <Send size={16}/></>}
                </button>
              </div>

              {state.ok && (
                <div className="mt-6 border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 p-4 flex items-start gap-3" data-testid="contact-success">
                  <CheckCircle2 size={18} className="mt-0.5"/>
                  <div>
                    <div className="font-semibold text-white">Message received.</div>
                    <div className="text-sm">A RidgecX specialist will be in touch within one business day. A confirmation email is on its way.</div>
                  </div>
                </div>
              )}
              {state.err && (
                <div className="mt-6 border border-red-500/30 bg-red-500/10 text-red-300 p-4 flex items-start gap-3" data-testid="contact-error">
                  <AlertCircle size={18} className="mt-0.5"/>
                  <div>
                    <div className="font-semibold text-white">Could not send message.</div>
                    <div className="text-sm">{state.err}</div>
                  </div>
                </div>
              )}
            </motion.form>
          </div>
        </div>
      </section>

      <style>{`
        .ridge-input {
          width: 100%;
          background: #050505;
          border: 1px solid #27272a;
          color: #fff;
          padding: 12px 14px;
          font-family: 'IBM Plex Sans', sans-serif;
          font-size: 15px;
          outline: none;
          transition: border-color .2s ease;
          border-radius: 0;
        }
        .ridge-input:focus {
          border-color: #ff3333;
        }
        .ridge-input::placeholder { color: #52525b; }
        select.ridge-input { appearance: none; background-image: linear-gradient(45deg, transparent 50%, #ff3333 50%), linear-gradient(135deg, #ff3333 50%, transparent 50%); background-position: calc(100% - 18px) 50%, calc(100% - 13px) 50%; background-size: 5px 5px; background-repeat: no-repeat; }
      `}</style>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="overline block mb-2">{label}{required && <span className="text-[#ff3333] ml-1">*</span>}</span>
    {children}
  </label>
);
